import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";
import { PostcodeInput } from "@flow/frontend/member/components/PostcodeInput";
import { pluginRegistry } from "@flow/frontend/member/plugin_api";
import { Text } from "@flow/lib/i18n";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Alert, Button } from "@mui/material";
import * as React from "react";
import { mpdbListTarget, mpdbMultipleTarget } from "../../common/campaign_target_providers";
import { lookupTargetByMember } from "../../common/routes";

// Client might pass in encoded postscode with spaces replaced by + signs.
// Protect against this and other cases as applicable.
//
// Manual Dev Test: http://localhost:8180/act/${journey_uuid}&zip=BS4+3BQ
//
export const normalisePostCode = (input: string | undefined): string | undefined => {
  if (!input) {
    return input;
  }

  // Remove '+' signs and spaces, and then capitalize everything
  return input.replace(/\+|\s/g, "").toUpperCase();
};

pluginRegistry.registerCampaignTargetProvider({
  def: mpdbListTarget,
  memberTargetLookupWidget: (props) => {
    const [postcode, setPostcode] = React.useState(normalisePostCode(props.member.postcode) || "");
    const [showSpinner, setShowSpinner] = React.useState(false);
    const [showNoTarget, setShowNoTarget] = React.useState(false);

    // lookup prefilled value once at component mount
    React.useEffect(() => {
      if (postcode && postcode.length > 0) {
        lookupPostcode(postcode);
      }
    }, []);

    function lookupPostcode(val: string) {
      setPostcode(val);
      setShowSpinner(true);
      setShowNoTarget(false);

      const newMember = { ...props.member, postcode: val };

      props.onUpdateMember(newMember);

      lookupTargetByMember
        .call({
          member: newMember,
          config: props.config,
        })
        .then((v) => {
          setShowSpinner(false);

          switch (v.type) {
            case "target":
              // if lookup succeeded, pass to parent
              props.onGetTarget([v]);
              break;
            case "no_target":
              // member has entered enough information to resolve the campaign
              // target, but none has been found (probably no target in their area)
              setShowNoTarget(true);
              break;
            case "insufficient_member_data":
              // let the user keep typing...
              break;
          }
        });
    }

    return (
      <>
        <PostcodeInput label="Postcode" value={postcode} onChange={setPostcode} sx={{ backgroundColor: "white" }} />
        {showNoTarget && (
          <Alert
            severity="warning"
            dangerouslySetInnerHTML={{ __html: props.config.on_no_target_msg_to_member }}
            sx={{ marginBottom: "1.5rem" }}
          >
            {!props.config.on_no_target_msg_to_member && (
              <Text id="plugin.mpdatabase.no_target">
                Sorry, it looks like your representative cannot be contacted on this issue.
              </Text>
            )}
          </Alert>
        )}
        {showSpinner && <MemberLoadingSpinner />}
        {!showSpinner && (
          <Button fullWidth onClick={() => lookupPostcode(postcode)}>
            Find My Representative <ChevronRightIcon />
          </Button>
        )}
      </>
    );
  },
});

pluginRegistry.registerCampaignTargetProvider({
  def: mpdbMultipleTarget,
  memberTargetLookupWidget: (props) => {
    // lookup prefilled value once at component mount
    React.useEffect(() => {
      props.onGetTarget(
        props.config.map((m) => {
          return { name: m.role ? `${m.name}, ${m.role}` : m.name, unique_id: m.membership_id };
        })
      );
    }, []);

    return <></>;
  },
});
